import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { LogOut, RefreshCw, ShieldX } from 'lucide-react';
import { queryClient } from '@/app/query-client';
import { Button } from '@/components/ui/button';
import { httpClient } from '@/services/api-client';
import { getAdminScopes } from '@/services/auth-scope.service';
import { useAuthStore } from '@/stores/auth.store';
import env from '@/configs/env';
import { SessionWarning } from '@ai-doc-automation/ui';
import { applicationSession } from './application-session';
import type { PlatformAuthScope } from '@/models/auth';

interface AuthContextValue {
  signOut: () => void;
  refreshToken: () => Promise<void>;
  selectScope: (scopeKey: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  user: unknown;
  signOut: () => void;
  children: ReactNode;
}

interface IdentitySnapshot {
  sub: string;
  email: string;
  displayName: string;
}

type AuthBlockReason = 'access-denied' | 'verification-failed';

const DEV_ORG_TENANT_ID = 'a0000000-0000-0000-0000-000000000030';

function getDevAdminToken(email: string, role: 'platform_admin' | 'org_admin'): string {
  if (email.trim().toLowerCase() === 'multi@docautomation.io') {
    return 'dev-multi-scope-token';
  }

  return role === 'org_admin' ? 'dev-org-admin-token' : 'dev-platform-admin-token';
}

function getDevAdminSub(email: string, role: 'platform_admin' | 'org_admin'): string {
  const normalizedEmail = email.trim().toLowerCase();
  if (normalizedEmail === 'multi@docautomation.io') {
    return 'demo-multi-scope';
  }

  return role === 'org_admin' ? 'demo-org-admin' : 'demo-platform-admin';
}

function getFallbackAdminScopes(role: 'platform_admin' | 'org_admin'): PlatformAuthScope[] {
  return role === 'org_admin'
    ? [{
        scopeKey: `org_admin:${DEV_ORG_TENANT_ID}`,
        appKind: 'admin',
        role: 'org_admin',
        tenantId: DEV_ORG_TENANT_ID,
        tenantName: 'Dev Organization',
        label: 'Dev Organization Admin',
        isDefault: true,
      }]
    : [{
        scopeKey: 'platform_admin:platform',
        appKind: 'admin',
        role: 'platform_admin',
        label: 'Platform Admin',
        isDefault: true,
      }];
}

function pickScope<T extends { scopeKey: string }>(
  scopes: T[],
  preferredScopeKey: string | null,
  suggestedScopeKey?: string,
): T | null {
  if (preferredScopeKey) {
    const preferred = scopes.find((scope) => scope.scopeKey === preferredScopeKey);
    if (preferred) {
      return preferred;
    }
  }

  if (suggestedScopeKey) {
    const suggested = scopes.find((scope) => scope.scopeKey === suggestedScopeKey);
    if (suggested) {
      return suggested;
    }
  }

  return scopes[0] ?? null;
}

function shouldResetQueryCacheForAuthChange(
  nextIdentitySub: string | null,
  nextScopeKey: string | null,
): boolean {
  const { userInfo, activeScopeKey } = useAuthStore.getState();
  const previousIdentitySub = userInfo?.sub ?? null;

  return previousIdentitySub !== nextIdentitySub || activeScopeKey !== nextScopeKey;
}

function clearScopeQueryCache(refetchActive = false) {
  void queryClient.cancelQueries();
  queryClient.clear();

  if (refetchActive) {
    setTimeout(() => {
      void queryClient.invalidateQueries({ refetchType: 'active' });
    }, 0);
  }
}

function AdminAccessBlockedScreen({
  reason,
  onRetry,
  onSignOut,
}: {
  reason: AuthBlockReason;
  onRetry: () => void;
  onSignOut: () => void;
}) {
  const isAccessDenied = reason === 'access-denied';

  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(135deg,#0e0c0a_0%,#1c1916_48%,#2c2520_100%)] px-6 py-10 font-sans text-white">
      <div className="w-full max-w-[460px] rounded-[28px] border border-white/15 bg-white/[0.96] p-7 text-slate-950 shadow-[0_32px_80px_-36px_rgba(8,15,35,0.85)]">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
          <ShieldX className="h-6 w-6" aria-hidden="true" />
        </div>
        <div className="mt-5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Admin Console</div>
        <h1 className="mt-1 text-[30px] font-bold tracking-tight text-slate-950">
          {isAccessDenied ? 'Access denied' : 'Unable to verify access'}
        </h1>
        <p className="mt-3 text-[14px] leading-6 text-slate-600">
          {isAccessDenied
            ? 'This portal is restricted to platform and organization administrators. Sign out and use an admin account to continue.'
            : 'We could not confirm your admin permissions. Try again, or sign out and use an admin account.'}
        </p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {!isAccessDenied && (
            <Button type="button" variant="outline" className="h-11 flex-1 rounded-xl" onClick={onRetry}>
              <RefreshCw aria-hidden="true" />
              Try again
            </Button>
          )}
          <Button type="button" className="h-11 flex-1 rounded-xl" onClick={onSignOut}>
            <LogOut aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AuthProvider({ user, signOut, children }: AuthProviderProps) {
  const {
    setUser,
    setUserInfo,
    setLoading,
    setAvailableScopes,
    setActiveScope,
    clearScopes,
    activeScopeKey,
  } = useAuthStore();
  const [ready, setReady] = useState(false);
  const [authBlockReason, setAuthBlockReason] = useState<AuthBlockReason | null>(null);
  const isSigningOutRef = useRef(false);

  const applyScopeSelection = useCallback(
    (
      identity: IdentitySnapshot,
      scopes: PlatformAuthScope[],
      selectedScope: PlatformAuthScope | null,
    ) => {
      setAvailableScopes(scopes);
      setActiveScope(selectedScope);
      httpClient.setActiveTenantId(selectedScope?.tenantId ?? null);

      if (!selectedScope) {
        setUserInfo(null);
        return;
      }

      setUserInfo({
        ...identity,
        role: selectedScope.role,
        organizationTenantId: selectedScope.role === 'org_admin' ? selectedScope.tenantId : undefined,
      });
    },
    [setActiveScope, setAvailableScopes, setUserInfo],
  );

  const syncAuth = useCallback(
    async (forceRefresh = false) => {
      if (isSigningOutRef.current) {
        return;
      }

      try {
        if (env.authMode !== 'cognito') {
          const demoUser = useAuthStore.getState().demoUser;
          const demoEmail = demoUser?.email ?? (user as { username?: string } | null)?.username ?? 'admin@embracelabs.io';
          const rawRole = demoUser?.role ?? ((user as {
            role?: 'platform_admin' | 'org_admin'
          } | null)?.role ?? 'platform_admin');
          const role = rawRole === 'org_admin' ? 'org_admin' : 'platform_admin';
          const identity: IdentitySnapshot = {
            sub: getDevAdminSub(demoEmail, role),
            email: demoEmail,
            displayName: demoEmail === 'multi@docautomation.io'
              ? 'Multi Scope User'
              : role === 'org_admin'
                ? 'Organization Admin'
                : 'Platform Admin',
          };

          httpClient.setToken(getDevAdminToken(demoEmail, role));
          setUser(user);

          try {
            const { scopes, suggestedScopeKey } = await getAdminScopes();
            if (scopes.length > 0) {
              const selectedScope = pickScope(scopes, activeScopeKey, suggestedScopeKey);
              const shouldClearCache = shouldResetQueryCacheForAuthChange(
                identity.sub,
                selectedScope?.scopeKey ?? null,
              );
              applyScopeSelection(identity, scopes, selectedScope);
              setAuthBlockReason(null);
              if (shouldClearCache) {
                clearScopeQueryCache();
              }
              return;
            }
          } catch (err) {
            console.warn('Falling back to static dev admin scopes:', err);
          }

          const fallbackScopes = getFallbackAdminScopes(role);
          const shouldClearCache = shouldResetQueryCacheForAuthChange(
            identity.sub,
            fallbackScopes[0]?.scopeKey ?? null,
          );
          applyScopeSelection(identity, fallbackScopes, fallbackScopes[0]);
          setAuthBlockReason(null);
          if (shouldClearCache) {
            clearScopeQueryCache();
          }
          return;
        }

        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession({ forceRefresh });

        if (isSigningOutRef.current) {
          return;
        }

        const idToken = session.tokens?.idToken;

        if (!idToken) {
          clearScopes();
          httpClient.clearToken();
          setUser(null);
          setUserInfo(null);
          clearScopeQueryCache();
          setAuthBlockReason('verification-failed');
          return;
        }

        httpClient.setToken(idToken.toString());
        await applicationSession.start();
        setUser(user);

        const payload = idToken.payload as Record<string, unknown>;
        const identity: IdentitySnapshot = {
          sub: (payload.sub as string) ?? '',
          email: (payload.email as string) ?? '',
          displayName: (payload.name as string) || (payload.email as string) || '',
        };
        const { scopes, suggestedScopeKey } = await getAdminScopes();

        if (!applicationSession.isActive()) return;

        if (scopes.length === 0) {
          clearScopes();
          httpClient.clearToken();
          setUser(null);
          setUserInfo(null);
          clearScopeQueryCache();
          setAuthBlockReason('access-denied');
          return;
        }

        const selectedScope = pickScope(scopes, activeScopeKey, suggestedScopeKey);
        const shouldClearCache = shouldResetQueryCacheForAuthChange(
          identity.sub,
          selectedScope?.scopeKey ?? null,
        );
        applyScopeSelection(identity, scopes, selectedScope);
        setAuthBlockReason(null);
        if (shouldClearCache) {
          clearScopeQueryCache();
        }
      } catch (err) {
        console.error('Failed to sync auth session:', err);
        clearScopes();
        httpClient.clearToken();
        setUser(null);
        setUserInfo(null);
        clearScopeQueryCache();
        if (env.authMode === 'cognito' && !isSigningOutRef.current) {
          setAuthBlockReason('verification-failed');
        }
      } finally {
        setLoading(false);
        setReady(true);
      }
    },
    [activeScopeKey, applyScopeSelection, clearScopes, setLoading, setUser, setUserInfo, user],
  );

  useEffect(() => {
    let cancelled = false;

    if (user) {
      void Promise.resolve().then(() => {
        if (!cancelled) {
          void syncAuth();
        }
      });
    } else {
      queueMicrotask(() => {
        if (cancelled) {
          return;
        }

        clearScopes();
        httpClient.clearToken();
        setUser(null);
        setUserInfo(null);
        clearScopeQueryCache();
        setAuthBlockReason(null);
        setLoading(false);
        setReady(true);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [clearScopes, setLoading, setUser, setUserInfo, syncAuth, user]);

  const refreshToken = useCallback(async () => {
    await syncAuth(true);
  }, [syncAuth]);

  const selectScope = useCallback(async (scopeKey: string) => {
    const state = useAuthStore.getState();
    const selectedScope = state.availableScopes.find((scope) => scope.scopeKey === scopeKey) ?? null;
    const currentUser = state.userInfo;

    if (!selectedScope || !currentUser) {
      return;
    }

    httpClient.setActiveTenantId(selectedScope.tenantId ?? null);
    state.setActiveScope(selectedScope);
    state.setUserInfo({
      ...currentUser,
      role: selectedScope.role,
      organizationTenantId: selectedScope.role === 'org_admin' ? selectedScope.tenantId : undefined,
    });
    clearScopeQueryCache(true);
  }, []);

  const handleSignOut = useCallback(() => {
    isSigningOutRef.current = true;
    if (env.authMode === 'cognito') applicationSession.end();
    clearScopeQueryCache();
    httpClient.clearToken();
    clearScopes();
    setAuthBlockReason(null);
    useAuthStore.getState().signOut();
    signOut();
  }, [clearScopes, signOut]);

  useEffect(() => {
    if (env.authMode !== 'cognito') return;
    applicationSession.setLogout(handleSignOut);
    const onExpired = () => applicationSession.expire();
    window.addEventListener('app-session-expired', onExpired);
    return () => {
      applicationSession.setLogout(null);
      window.removeEventListener('app-session-expired', onExpired);
    };
  }, [handleSignOut]);

  if (!ready) return null;

  if (authBlockReason) {
    return (
      <AdminAccessBlockedScreen
        reason={authBlockReason}
        onRetry={() => {
          void syncAuth(true);
        }}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <AuthContext.Provider value={{ signOut: handleSignOut, refreshToken, selectScope }}>
      {env.authMode === 'cognito' ? <SessionWarning session={applicationSession}>{children}</SessionWarning> : children}
    </AuthContext.Provider>
  );
}

export function useAuthActions() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthActions must be inside AuthProvider');
  return ctx;
}
